import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { HtmlWidgetSchema, type HtmlWidget } from '@inference.engine/widgets/web';
import { getWidgetStores } from '@/entities/widget';
import { SidePanel } from '@/shared/ui/SidePanelWrapper';
import { useWidgetActions, type WidgetActionSource } from '../../model/useWidgetActions';
import { HtmlWidgetFrame } from '../HtmlWidgetFrame/HtmlWidgetFrame';

function PanelFrame({ source, contextId }: { source: WidgetActionSource; contextId?: string }) {
  const onAction = useWidgetActions(source);
  return (
    // The side panel clips its contents and leaves scrolling to the widget.
    <div data-testid="html-widget-panel" className="h-full overflow-y-auto p-4">
      <HtmlWidgetFrame
        widget={source.widget}
        artifactId={source.artifactId}
        contextId={contextId}
        onAction={onAction}
      />
    </div>
  );
}

/**
 * A panel widget, as the side panel mounts it from the widget registry, in the side panel every
 * widget has: its header with the title, type, versions and close.
 */
export function HtmlWidgetPanel({ data }: { data: unknown }) {
  const { t } = useTranslation('chats');
  const { widgetStore, chatStore } = getWidgetStores();
  const { widget: stored } = widgetStore.useWidgetPanel();
  const versionConfig = widgetStore.useWidgetVersions(stored?.agentName ?? '');
  const contextId = chatStore.useChatId() ?? undefined;
  const parsed = useMemo(() => HtmlWidgetSchema.safeParse(data), [data]);

  if (!parsed.success || !stored) {
    return <p className="p-6 text-sm text-muted-foreground">{t('chats.htmlWidget.unreadable')}</p>;
  }
  const widget: HtmlWidget = parsed.data;
  const source: WidgetActionSource = {
    key: typeof stored.key === 'string' ? stored.key : '',
    agentName: stored.agentName,
    artifactId: typeof stored.artifactId === 'string' ? stored.artifactId : undefined,
    widget,
  };
  return (
    <SidePanel
      title={widget.title ?? t('chats.htmlWidget.title')}
      headerConfig={{
        chip: { label: t('chats.htmlWidget.title'), color: 'blue' },
        ...(versionConfig ? { versionConfig } : {}),
      }}
    >
      <PanelFrame source={source} contextId={contextId} />
    </SidePanel>
  );
}
